import type { MemphisAssetOperationalStatus } from './memphisThreatNotification';
import type {
  JapanBedStatus,
  JapanCountry,
  JapanImpact,
} from './japanEarthquakeNotification';

/**
 * Canned bilingual SITREP for the Japan demo. The Japanese is pre-written to
 * the same structure as the English; nothing is translated at runtime and no
 * OSINT source is queried.
 */

export type JapanSitrepLang = 'en' | 'ja';

export const JAPAN_SITREP_SECTION_IDS = [
  'situation',
  'assets',
  'osint',
  'partners',
  'response',
] as const;

export type JapanSitrepSectionId = (typeof JAPAN_SITREP_SECTION_IDS)[number];

export type JapanOsintItem = {
  id: string;
  sourceType: string;
  source: string;
  time: string;
  summary: string;
};

export type JapanPartner = {
  id: string;
  country: JapanCountry;
  name: string;
  role: string;
};

export type JapanResponseObjective = {
  id: string;
  lineOfEffort: string;
  division: string;
  text: string;
};

export type JapanSitrepContent = {
  title: string;
  asOf: string;
  translatedNote: string | null;
  sections: { id: JapanSitrepSectionId; heading: string }[];
  situation: string[];
  assetsLegend: string;
  assetColumns: {
    rank: string;
    asset: string;
    tier: string;
    impact: string;
    status: string;
    why: string;
  };
  impactLabels: Record<JapanImpact, string>;
  statusLabels: Record<MemphisAssetOperationalStatus, string>;
  bedLabels: Record<JapanBedStatus, string>;
  deconLabels: { yes: string; no: string };
  osintBadge: string;
  keyJudgementsHeading: string;
  keyJudgements: string;
  demoSourceTag: string;
  osint: JapanOsintItem[];
  partners: JapanPartner[];
  response: JapanResponseObjective[];
  showAll: (count: number) => string;
  showLess: string;
};

const EN: JapanSitrepContent = {
  title: 'SITREP 1 — Tokyo Bay earthquake',
  asOf: 'As of 23:15 JST, 2 Nov 2026',
  translatedNote: null,
  sections: [
    { id: 'situation', heading: 'Situation' },
    { id: 'assets', heading: 'Assets impacted, ranked' },
    { id: 'osint', heading: 'AI-summarized OSINT' },
    { id: 'partners', heading: 'Partners' },
    { id: 'response', heading: 'Response plan' },
  ],
  situation: [
    'At 21:47 JST a M7.3 earthquake struck northern Tokyo Bay. JMA intensity reached upper 6 in Kawasaki-ku and Ota-ku.',
    'Two storage tanks at the Keihin Coastal Chemical Complex in Kawasaki failed, releasing chlorine and ammonia. The plume is drifting north-east over the Haneda approaches. The release is tracked as a separate event, linked to the earthquake as related.',
    'Haneda runways C and D are closed and arrivals are diverting to Narita. The Port of Kawasaki is closed inside an exclusion zone. Japan has requested HADR support, and the Embassy has started NEO planning.',
  ],
  assetsLegend:
    'Priority = criticality × impact. Tier 1 = 3, Tier 2 = 2, Tier 3 = 1; Low = 1 to Severe = 4.',
  assetColumns: {
    rank: '#',
    asset: 'Asset',
    tier: 'Tier',
    impact: 'Impact',
    status: 'Status',
    why: 'Why',
  },
  impactLabels: {
    Low: 'Low',
    Medium: 'Medium',
    High: 'High',
    Severe: 'Severe',
  },
  statusLabels: {
    Operational: 'Operational',
    'Partially Operational': 'Partially operational',
    'Not Operational': 'Not operational',
  },
  bedLabels: {
    Open: 'Beds open',
    Limited: 'Beds limited',
    Diverting: 'Diverting',
  },
  deconLabels: { yes: 'Decon', no: 'No decon' },
  osintBadge: 'AI-summarized',
  keyJudgementsHeading: 'Key judgements',
  keyJudgements:
    'The chemical release, not the shaking, is driving the disruption: it closes Haneda’s southern runways and the Port of Kawasaki. Yokota and Narita are undamaged and can carry the NEO. Decontamination-capable hospital beds near the release are the binding constraint.',
  demoSourceTag: 'Demo source',
  osint: [
    {
      id: 'jma',
      sourceType: 'Official',
      source: 'Japan Meteorological Agency',
      time: '21:49 JST',
      summary:
        'M7.3, depth 30 km, northern Tokyo Bay. Upper 6 in Kawasaki-ku and Ota-ku. Tsunami advisory for Tokyo Bay.',
    },
    {
      id: 'nhk',
      sourceType: 'Media',
      source: 'NHK',
      time: '22:05 JST',
      summary:
        'White vapour and fire reported at a chemical complex on the Kawasaki waterfront. Residents told to stay indoors.',
    },
    {
      id: 'kawasaki-port',
      sourceType: 'Official',
      source: 'Kawasaki Port Authority',
      time: '22:20 JST',
      summary:
        'Port of Kawasaki closed to all vessel traffic. Exclusion zone in effect around the waterfront.',
    },
    {
      id: 'airlines',
      sourceType: 'Industry',
      source: 'Airline notices, Haneda',
      time: '22:35 JST',
      summary:
        'Departures from Haneda cancelled for the night. Arrivals diverting to Narita after runways C and D close.',
    },
    {
      id: 'social',
      sourceType: 'Social media',
      source: 'Public posts, Kawasaki-ku',
      time: '22:40 JST',
      summary:
        'Cluster of posts reporting a chlorine smell and eye irritation near Daishi. Unverified, but consistent with the plume model.',
    },
    {
      id: 'kanagawa',
      sourceType: 'Official',
      source: 'Kanagawa Prefecture',
      time: '22:50 JST',
      summary:
        'Shelter-in-place advisory for coastal Kawasaki-ku. Evacuation shelters opened inland.',
    },
    {
      id: 'embassy',
      sourceType: 'Official',
      source: 'US Embassy Tokyo',
      time: '23:10 JST',
      summary:
        'Message for US citizens: follow local guidance and register for departure assistance.',
    },
  ],
  partners: [
    {
      id: 'usfj',
      country: 'US',
      name: 'US Forces Japan',
      role: 'Joint coordination, airlift',
    },
    {
      id: 'embassy',
      country: 'US',
      name: 'US Embassy Tokyo',
      role: 'NEO decision, citizen messaging',
    },
    {
      id: 'yokosuka',
      country: 'US',
      name: 'Fleet Activities Yokosuka',
      role: 'HADR staging',
    },
    {
      id: 'jsdf',
      country: 'JP',
      name: 'JSDF Eastern Army',
      role: 'HADR lead, damage assessment',
    },
    {
      id: 'kanagawa',
      country: 'JP',
      name: 'Kanagawa Prefecture',
      role: 'Disaster headquarters',
    },
    {
      id: 'kawasaki',
      country: 'JP',
      name: 'Kawasaki City Fire Bureau',
      role: 'Hazmat, shelter in place',
    },
    {
      id: 'red-cross',
      country: 'JP',
      name: 'Japanese Red Cross',
      role: 'Medical relief teams',
    },
  ],
  response: [
    {
      id: 'survey',
      lineOfEffort: 'All',
      division: 'All divisions',
      text: 'Conduct survey of all defense critical infrastructure and rank sites by criticality × impact.',
    },
    {
      id: 'plume',
      lineOfEffort: 'HADR',
      division: 'Division A — Kawasaki',
      text: 'Confirm the plume extent and stage decontamination for exposure casualties.',
    },
    {
      id: 'haneda',
      lineOfEffort: 'HADR',
      division: 'Division B — Haneda',
      text: 'Divert military arrivals to Narita and restore Haneda runways once the plume clears.',
    },
    {
      id: 'honmoku',
      lineOfEffort: 'HADR',
      division: 'Division C — Yokohama',
      text: 'Clear Honmoku berths to take relief shipping diverted from Kawasaki.',
    },
    {
      id: 'yokosuka',
      lineOfEffort: 'HADR',
      division: 'Division D — Yokosuka',
      text: 'Stage relief supplies and open surge beds at Yokosuka.',
    },
    {
      id: 'ecc',
      lineOfEffort: 'NEO',
      division: 'Division E — Yokota',
      text: 'Stand up the evacuation control centre and run the airlift through Yokota, with Narita as the alternate.',
    },
    {
      id: 'warden',
      lineOfEffort: 'NEO',
      division: 'Division E — Yokota',
      text: 'Message US citizens and register them for departure assistance.',
    },
  ],
  showAll: (count) => `Show all ${count}`,
  showLess: 'Show less',
};

const JA: JapanSitrepContent = {
  title: '状況報告 第1報 — 東京湾地震',
  asOf: '2026年11月2日 23:15 JST 現在',
  translatedNote: '英語から翻訳（デモ） / Translated from English (demo)',
  sections: [
    { id: 'situation', heading: '状況' },
    { id: 'assets', heading: '被害施設（優先順位）' },
    { id: 'osint', heading: 'AI要約 OSINT' },
    { id: 'partners', heading: '連携機関' },
    { id: 'response', heading: '対応計画' },
  ],
  situation: [
    '21時47分、東京湾北部を震源とするM7.3の地震が発生。川崎区と大田区で震度6強を観測した。',
    '川崎の京浜臨海化学コンビナートで貯蔵タンク2基が損傷し、塩素とアンモニアが漏えい。拡散域は北東方向へ流れ、羽田空港の進入経路上空に達している。この漏えいは別のイベントとして管理し、地震の関連イベントとして紐付けている。',
    '羽田空港のC・D滑走路は閉鎖され、到着便は成田へ代替着陸している。川崎港は立入禁止区域内で閉鎖中。日本政府はHADR支援を要請し、米国大使館はNEOの計画を開始した。',
  ],
  assetsLegend:
    '優先度 = 重要度 × 被害度。Tier 1 = 3、Tier 2 = 2、Tier 3 = 1。低 = 1 〜 甚大 = 4。',
  assetColumns: {
    rank: '順位',
    asset: '施設',
    tier: '重要度',
    impact: '被害度',
    status: '状態',
    why: '理由',
  },
  impactLabels: { Low: '低', Medium: '中', High: '高', Severe: '甚大' },
  statusLabels: {
    Operational: '運用中',
    'Partially Operational': '一部運用',
    'Not Operational': '運用停止',
  },
  bedLabels: { Open: '病床あり', Limited: '病床制限', Diverting: '受入停止' },
  deconLabels: { yes: '除染可', no: '除染不可' },
  osintBadge: 'AI要約',
  keyJudgementsHeading: '主な評価',
  keyJudgements:
    '混乱の主因は揺れではなく化学物質の漏えいであり、羽田空港の南側滑走路と川崎港を閉鎖させている。横田と成田は被害がなく、NEOを担える。漏えい現場周辺で除染可能な病床が最大の制約となっている。',
  demoSourceTag: 'デモ用情報源',
  osint: [
    {
      id: 'jma',
      sourceType: '公的機関',
      source: '気象庁',
      time: '21:49 JST',
      summary:
        'M7.3、深さ30km、東京湾北部。川崎区・大田区で震度6強。東京湾に津波注意報。',
    },
    {
      id: 'nhk',
      sourceType: '報道',
      source: 'NHK',
      time: '22:05 JST',
      summary:
        '川崎臨海部の化学コンビナートで白煙と火災。住民に屋内退避を呼びかけ。',
    },
    {
      id: 'kawasaki-port',
      sourceType: '公的機関',
      source: '川崎港管理者',
      time: '22:20 JST',
      summary: '川崎港は全船舶の入出港を停止。臨海部周辺に立入禁止区域を設定。',
    },
    {
      id: 'airlines',
      sourceType: '業界',
      source: '航空会社の案内（羽田）',
      time: '22:35 JST',
      summary:
        '羽田発の便は本日欠航。C・D滑走路閉鎖に伴い到着便は成田へ代替着陸。',
    },
    {
      id: 'social',
      sourceType: 'SNS',
      source: '一般投稿（川崎区）',
      time: '22:40 JST',
      summary:
        '大師周辺で塩素臭や目の刺激を訴える投稿が集中。未確認だが拡散モデルと整合。',
    },
    {
      id: 'kanagawa',
      sourceType: '公的機関',
      source: '神奈川県',
      time: '22:50 JST',
      summary: '川崎区臨海部に屋内退避を勧告。内陸部に避難所を開設。',
    },
    {
      id: 'embassy',
      sourceType: '公的機関',
      source: '在日米国大使館',
      time: '23:10 JST',
      summary: '米国市民向け案内：現地の指示に従い、出国支援に登録すること。',
    },
  ],
  partners: [
    {
      id: 'usfj',
      country: 'US',
      name: '在日米軍',
      role: '統合調整、空輸',
    },
    {
      id: 'embassy',
      country: 'US',
      name: '在日米国大使館',
      role: 'NEOの決定、米国市民への周知',
    },
    {
      id: 'yokosuka',
      country: 'US',
      name: '横須賀海軍施設',
      role: 'HADR物資の集積',
    },
    {
      id: 'jsdf',
      country: 'JP',
      name: '陸上自衛隊 東部方面隊',
      role: 'HADRの主導、被害調査',
    },
    {
      id: 'kanagawa',
      country: 'JP',
      name: '神奈川県',
      role: '災害対策本部',
    },
    {
      id: 'kawasaki',
      country: 'JP',
      name: '川崎市消防局',
      role: '危険物対応、屋内退避',
    },
    {
      id: 'red-cross',
      country: 'JP',
      name: '日本赤十字社',
      role: '医療救護班',
    },
  ],
  response: [
    {
      id: 'survey',
      lineOfEffort: '全体',
      division: '全区域',
      text: '全ての防衛重要インフラを調査し、重要度 × 被害度で優先順位を付ける。',
    },
    {
      id: 'plume',
      lineOfEffort: 'HADR',
      division: 'A区域 — 川崎',
      text: '拡散域の範囲を確認し、曝露患者の除染体制を整える。',
    },
    {
      id: 'haneda',
      lineOfEffort: 'HADR',
      division: 'B区域 — 羽田',
      text: '軍用到着便を成田へ振り替え、拡散域が解消し次第、羽田の滑走路を再開する。',
    },
    {
      id: 'honmoku',
      lineOfEffort: 'HADR',
      division: 'C区域 — 横浜',
      text: '本牧ふ頭のバースを確保し、川崎から振り替える救援船を受け入れる。',
    },
    {
      id: 'yokosuka',
      lineOfEffort: 'HADR',
      division: 'D区域 — 横須賀',
      text: '横須賀に救援物資を集積し、増床を開始する。',
    },
    {
      id: 'ecc',
      lineOfEffort: 'NEO',
      division: 'E区域 — 横田',
      text: '退避統制センターを立ち上げ、横田を拠点に空輸を実施する（代替は成田）。',
    },
    {
      id: 'warden',
      lineOfEffort: 'NEO',
      division: 'E区域 — 横田',
      text: '米国市民に周知し、出国支援への登録を受け付ける。',
    },
  ],
  showAll: (count) => `全${count}件を表示`,
  showLess: '折りたたむ',
};

export const JAPAN_SITREP_CONTENT: Record<JapanSitrepLang, JapanSitrepContent> =
  { en: EN, ja: JA };

export function getJapanSitrepContent(
  lang: JapanSitrepLang
): JapanSitrepContent {
  return JAPAN_SITREP_CONTENT[lang];
}
